/**
 * <TutorOffersHeader> — le haut de l'onglet « Offres de cours » (organism).
 *
 * Legacy `ProposalsTabScreen`, au-dessus de la liste : le titre et la relecture, le rappel « Confirme
 * tes créneaux » quand la semaine est à re-confirmer (il mène à « Ma dispo »), et les onglets — un
 * par type d'offre que le masteur a le droit de voir, chacun avec le compte de ses offres en attente.
 * Ce sont de vrais onglets (`<Tabs>`, Wajih, 03/11) : ils changent la liste montrée, ils ne
 * répondent à aucune question. Trop d'onglets pour la largeur : la rangée défile, jamais d'abréviation.
 *
 * Le rappel est la carte entière qui se touche, avec son chevron, comme dans le legacy ; son ton est
 * celui d'une alerte douce (le liseré orange), pas d'une faute.
 */
import React from "react"
import { StyleSheet } from "react-native-unistyles"

import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Icon } from "../../atoms/icons/Icon"
import { Text } from "../../atoms/typography/Text"
import { Title } from "../../atoms/typography/Title"
import { Card } from "../../molecules/Card"
import { RefreshButton } from "../../molecules/RefreshButton"
import { Tabs } from "../../molecules/Tabs"
import { type UITutorOfferKind, type UITutorOfferTab } from "../../../ports/primary/ui/types/tutorDispoOffres"

const TAB_LABEL: Record<UITutorOfferKind, string> = {
    group: "Groupes",
    trial: "Essais",
    substitution: "Remplacements",
}

const TEXTS = {
    title: "Offres de cours",
    tabs: "Type d'offre",
    nudgeTitle: "Confirme tes créneaux",
    nudgeBody: "Une fois par semaine, pour continuer à recevoir des propositions.",
}

export type TutorOffersHeaderProps = {
    tabs: UITutorOfferTab[]
    activeKind: UITutorOfferKind
    onSelectTab: (kind: UITutorOfferKind) => void
    /** La semaine est à re-confirmer : le rappel se montre. */
    dispoStale?: boolean
    onOpenDispo?: () => void
    onRefresh?: () => void
    refreshing?: boolean
    testID?: string
}

export function TutorOffersHeader(props: TutorOffersHeaderProps) {
    const {
        tabs,
        activeKind,
        onSelectTab,
        dispoStale = false,
        onOpenDispo,
        onRefresh,
        refreshing = false,
        testID,
    } = props

    return (
        <Stack gap="4" testID={testID}>
            <Row justify="between" align="center">
                <Title size="text-2xl">{TEXTS.title}</Title>
                {onRefresh != null && <RefreshButton refreshing={refreshing} onRefresh={onRefresh} />}
            </Row>
            {dispoStale && (
                <Card pressable onPress={onOpenDispo} leftBorderColor="warning" p="4">
                    <Row gap="3" align="center">
                        <Icon name="clock" size={20} color="text-warning" />
                        <Stack gap="0" style={styles.flex}>
                            <Text size="text-md" weight="bold">
                                {TEXTS.nudgeTitle}
                            </Text>
                            <Text size="text-md" color="text-muted">
                                {TEXTS.nudgeBody}
                            </Text>
                        </Stack>
                        <Icon name="chevron-right" size={16} color="text-warning" />
                    </Row>
                </Card>
            )}
            {tabs.length > 0 && (
                <Tabs
                    items={tabs.map((tab) => ({ key: tab.kind, label: TAB_LABEL[tab.kind], count: tab.pendingCount }))}
                    value={activeKind}
                    onChange={onSelectTab}
                    fadeColor="surface-subtle"
                    accessibilityLabel={TEXTS.tabs}
                />
            )}
        </Stack>
    )
}

const styles = StyleSheet.create(() => ({
    flex: { flex: 1, flexShrink: 1 },
}))
